<template>
  <SubLayout
      :pageTitle="$t('reports.title')"
      :pageBreadcrumbs="breadcrumbs"
      :hasBack="false"
  >

    <template #layoutContent>
      <Card card="card p-5 bg-light" :title="$t('reports.liste')">
        <template #cardToolbar>
          <ButtonDropdownFilter
              :btn-title="$t('label.allContacts')"
              :options="[...contacts]"
              value-field="id"
              name-field="fullname"
              v-model="query.contact_id"
          />
          <ButtonDropdownFilter
              :btn-title="$t('projects.label.cat')"
              :options="[...categories]"
              value-field="id"
              name-field="name"
              v-model="query.project_category_id"
          />
          <ButtonDropdownFilter
              :btn-title="$t('projects.label.users')"
              :options="users"
              value-field="id"
              name-field="fullname"
              v-model="query.user_id"
          />

          <ButtonDropdownFilter
              :search="false"
              :options="status"
              value-field="value"
              :name-field="locale"
              v-model="query.status"
          />
          <DateRangePicker id="date_id" @update:date="handleRangeDate" with-all-time />
          <Input
              container-class="me-2 mb-2 mb-md-0"
              inputClass="form-control form-control-sm"
              name="input-search"
              :isInputGroup="true"
              :placeholder="$t('label.search')"
              v-model="query.search"
          >
            <template #rightAddon>
              <span class="position-absolute border-0 bg-transparent end-00 top-50 translate-middle">
                <i class="fa fa-search"></i>
              </span>
            </template>
          </Input>
          <Button
              type="button"
              btn-class="btn-sm btn-danger mx-3"
              btn-text="button.reset"
              icon-class="fa fa-filter fs-2"
              v-if="hasFilters"
              @click.prevent="resetFilters" />
          <div class="bg-white shadow justify-content-center align-items-center p-1 me-3 rounded-2 border border-primary">
            <a  @click="changeModelViewType(modelViewType.list)"
              :class="(route?.query?.viewType == modelViewType.list ) ? 'active' : ''"
              class="btn btn-sm btn-outline-primary text-active-white text-hover-white btn-icon"
              :title="$t('projects.list')"
            >
              <i class="bi bi-list-task fs-6 text-primary"></i> 
            </a>
            <a @click="changeModelViewType(modelViewType.grid)"
              :class="(route.query.viewType == modelViewType.grid || !route.query.viewType) ? 'active' : ''"
              class="btn btn-sm btn-outline-primary text-active-white text-hover-white btn-icon"
              :title="$t('projects.grid')"
            >
              <i class="bi bi-grid-fill fs-6 text-primary"></i>
            </a>
          </div>
        </template>
        <div class="card-body p-0">
          <ScrollableListContainer
              v-if="(route.query.viewType == modelViewType.grid || !route.query.viewType)"
              id="scrollable-list-container"
              :loading="loading"
              :length="projects?.length"
              :empty-text="$t('reports.noData')"
              :current-page="projectsMeta.current_page"
              :last-page="projectsMeta.last_page"
              @load-next-page="loadNextPage"
              container-height="70vh"
          >
            <div :class="'col-md-6 col-lg-4 mb-3'" v-for="(project,index) in projects"  :key="project?.id">
              <FolderCard
                  :id="index"
                  :selected="false"
                  :project="project"
                  :handleProjectSelected="handleProjectSelected"
                  @reloadData="reloadProjectCards"
              />
            </div>
          </ScrollableListContainer>
          <ReportListView v-if="(route.query.viewType == modelViewType.list)" :filter="query"  @reloadDatatable='loadData' />
        </div>
      </Card>

      <ReportModal
          id="report_detail_modal"
          :report="current"
          :contacts="contactsWithStatus"
          :getting="getting"
          @load="loadProjectSelected" 
      />
    </template>
  </SubLayout>
</template> 

<script setup>
import ButtonDropdownFilter from '@/components/ButtonDropdownFilter.vue';
import Card from '@/components/Card.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import { useLocales } from '@/composables/locales';
import SubLayout from '@/views/Layouts/partials/SubLayout.vue';
import { computed, onMounted, watch } from 'vue';
import { ref } from 'vue';
import { useStore } from 'vuex';
import FolderCard from './FolderCard.vue';
import Input from '@/components/Input.vue';
import { useModelViewType } from '@/composables/modelViewType';
import { useRoute, useRouter } from 'vue-router';
import ReportListView from './ReportListView.vue';
import ReportModal from './ReportModal.vue';
import ScrollableListContainer from "@/components/ScrollableListContainer.vue";
import {useFilters} from "@/composables/useFilters";
import filterKeys from "@/composables/filterKeys";
import Button from "@/components/Button.vue";

const {locale} = useLocales()
const store = useStore()
const router = useRouter()
const route = useRoute()
const { modelViewType } = useModelViewType()

const loading = ref(false)


const query = ref({
  search: "",
  contact_id: "",
  project_category_id: "",
  status: "all",
  start_date: "",
  end_date: "",
  user_id: '',
})
const getting = ref(false)
const current = ref('')
const contactsWithStatus = ref([])
const selectedOffice = ref('')

const contacts = computed(() => store.getters['contacts/getContacts'])
const categories = computed(() => store.getters['projectCategories/getProjectCategories'])
const users = computed(() => store.getters["user/getUsers"])
const status = computed(() => store.getters["projects/getProjectStatus"])
const projects = computed(() => store.getters["projects/getProjectReports"])
const projectsMeta = computed(() => store.getters["projects/getProjectReportsMeta"])

watch(()=> [query.value, selectedOffice.value], () => {

  if (JSON.stringify(query.value) !== JSON.stringify(oldFilters.value)) {
    updateFilter(query.value)
  }

  projectCards(query.value)

  router.replace({
    name: route.name,
    params: { ...route.params },
    query: {...route.query, filter: JSON.stringify(query.value)},
  })
}, {deep: true})

const handleRangeDate = (range) => {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
}

function projectCards(filter) {
  loading.value = true
  const data = {...filter, status: filter?.status !== 'all' ? filter.status  : '', office: selectedOffice.value}

  store.dispatch("projects/loadProjectReport", data)
      .then(() => {}).finally(() => loading.value = false)
}

const loadNextPage = (page) => projectCards({...query.value, page});

function reloadProjectCards () {
  const data = {...query.value, status: query.value.status !== 'all' ? query.value.status  : '', office: selectedOffice.value}

  store.dispatch("projects/loadProjectReport", data)
      .then(() =>{})
      .catch(() =>{})
      .finally(() =>{})
}

function changeModelViewType (viewType) {
  let toSet = viewType ?? modelViewType.grid
  router.push({ query: { viewType: toSet } });
}

const loadData = () => {
  Promise.all([
    store.dispatch('contacts/loadContacts'),
    store.dispatch('user/loadUsers'),
    store.dispatch("projectCategories/loadProjectCategories"),
    store.dispatch("projects/loadProjectStatus"),
    store.dispatch('projects/loadProjectReactionEnum'),
  ])
}

const handleProjectSelected = (id) => {
  try {
    getting.value = true
    $('#report_detail_modal').modal('toggle')
    store.dispatch('projects/getProject', {
      id : id,
      user_id :  query.value.user_id
    })
        .then((res) => {
          current.value = res.data
          contactsWithStatus.value = getContactsWithStatus(id)
        }).finally(() => getting.value = false)

  } catch (error) {

  }
}

const getContactsWithStatus = (id) => {
  let report = projects.value?.filter(el => el?.id == id)
  if(report?.length > 0){
    return report[0]?.contact?.map(c => ({name: c.contact_name, status: c.status_name}))
  }
  return []
}

const loadProjectSelected = (id) => {
  reloadProjectCards()
   try {
    getting.value = true
    store.dispatch('projects/getProject', {
      id : id,
      user_id :  query.value.user_id
    })
    .then((res) => {
      current.value = res.data
    }).finally(() => getting.value = false)
  } catch (error) {

  }

}

function resetFilters() {
  query.value = {
    search: "",
    contact_id: "",
    project_category_id: "",
    status: "all",
    start_date: "",
    end_date: "",
    user_id: '',
  }
}

const { filters: oldFilters, hasFilters, updateFilter } = useFilters(filterKeys.PROJECT_REPORT, query.value)

onMounted(() => {

  store.dispatch('projects/resetProjectReports')

  loadData()

  if(oldFilters.value && JSON.stringify(oldFilters.value) !== JSON.stringify(query.value)) {
    query.value = {
      search: oldFilters.value?.search || '',
      contact_id: oldFilters.value?.contact_id || '',
      start_date: oldFilters.value?.start_date || '',
      end_date: oldFilters.value?.end_date || '',
      project_category_id: oldFilters.value?.project_category_id || '',
      status: oldFilters.value?.status || 'all',
      user_id: oldFilters.value?.user_id || ''
    }
  }

  if(query.value.start_date && query.value.end_date) projectCards(query.value)

  loadNextPage()

  router.push({ query: { viewType : modelViewType.grid } });

  if (route.query?.filter) {
      query.value = {...JSON.parse(route.query?.filter)}
  }

})
</script>
<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.end-0{
  right: -3% !important;
}
.end-00{
  right: 0% !important;
}
button:not(.collapsed)::after {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e") !important;
  transform: rotate(-180deg) !important;
}

.accordion-item {
  background-color: #fff;
  border: 1px solid var(--bs-gray-300);
  border-radius: 6px !important;

}
.accordion-item:not(:first-of-type) {
  border: 1px solid var(--bs-gray-300);
  border-radius: 6px !important;
}

.accordion-button:not(.collapsed) {
  color: #fff;
  background-color: var(--bs-primary);
  box-shadow: inset 0 -1px 0 var(--bs-primary);
  border-radius: 6px !important;
}

.accordion-item:first-of-type {
  border: 1px solid var(--bs-gray-300);
  border-radius: 6px !important;
}

.accordion-item:last-of-type .accordion-button.collapsed {
  border-radius: 6px !important;
}

.accordion-item:not(:last-of-type) .accordion-button.collapsed {
  border-radius: 6px !important;
}

.vertical-line {
  width: 2px;
  height: 100%;
  background: var(--bs-gray-300);
  margin-right: 17px;
}

.hcontent {
  height: 100vh;
  overflow-y: auto;
  padding-right: 5px;
}
</style>